import { useState } from 'react';
import { FilesState, FormData } from '../types';

export function useFileUpload(
  startPPTTranscription: (file: File) => Promise<void>,
  initialValues?: FormData | null
) {
  const getFileNameFromUrl = (
    url: string | null | undefined,
    defaultName: string
  ) => {
    if (!url) return null;
    try {
      const parts = url.split('/');
      const lastPart = parts[parts.length - 1];
      const fileName = lastPart.split('?')[0];
      return fileName || defaultName;
    } catch {
      return defaultName;
    }
  };

  const initialPresentationUrl =
    (initialValues?.award_material as string | null) ||
    (initialValues?.detailed_fields as any)?.['Award Material'] ||
    null;

  const initialVideoUrl =
    (initialValues?.video_url as string | null) ||
    (initialValues?.detailed_fields as any)?.['Video URL'] ||
    null;

  const [files, setFiles] = useState<FilesState>({
    presentation: {
      url: initialPresentationUrl || null,
      fileName: getFileNameFromUrl(
        initialPresentationUrl,
        'Existing Presentation'
      ),
      uploading: false,
      isNewFile: false,
    },
    video: {
      url: initialVideoUrl || null,
      fileName: getFileNameFromUrl(initialVideoUrl, 'Existing Video'),
      uploading: false,
      isNewFile: false,
    },
  });

  // Called when a file is selected (before upload) — starts transcription in parallel with upload
  const handleFileSelected = (type: string) => (file: File) => {
    // Mark as new file so the form knows a fresh file was chosen
    setFiles(prev => ({
      ...prev,
      [type]: { ...prev[type as keyof FilesState], isNewFile: true },
    }));
    if (type === 'presentation') {
      startPPTTranscription(file);
    }
  };

  const handleFileUpload =
    (type: string) => async (url: string, fileName: string) => {
      setFiles(prev => ({
        ...prev,
        [type]: { url, fileName, uploading: false, isNewFile: true },
      }));
    };

  const handleFileUploadStart = (type: string) => {
    setFiles(prev => ({
      ...prev,
      [type]: { ...prev[type as keyof FilesState], uploading: true },
    }));
  };

  const handleFileUploadError = (type: string) => (error: unknown) => {
    console.error(`❌ QualificationForm: Upload failed for ${type}:`, error);
    setFiles(prev => ({
      ...prev,
      [type]: { ...prev[type as keyof FilesState], uploading: false },
    }));
  };

  return {
    files,
    handleFileSelected,
    handleFileUpload,
    handleFileUploadStart,
    handleFileUploadError,
  };
}
